import { createContext, useContext } from "react";
import styled, { createGlobalStyle, css } from "styled-components";
import { ButtonT1 } from "../../../../components/atoms/Button";
import { PortalFooterCard } from "../../../../components/atoms/Footer";
import { StyledFormInput } from "../../../../components/atoms/Input/Input.styles";
import { portalDrawerAntHeaderCss } from "../../../../components/molecules/Header/Header.styles";
import { tableHeaderTextCss } from "../../../../components/atoms/Table/tableTypography.styles";

export const ADD_ITEMS_DRAWER_ROOT_CLASS = "order-line-add-items-drawer";

/** PO create/update add-items drawer; enables striped product table without affecting sales orders. */
export const PO_ADD_ITEMS_DRAWER_ROOT_CLASS = "po-add-items-drawer";

export const addItemsTableStripeRowClassName = index =>
  index % 2 === 1 ? "order-line-table-row-striped" : "";

/** Product code column in the PO add-items and edit-multiple drawers. */
export const DRAWER_PRODUCT_COLUMN_WIDTH = 360;

/** Matches {@link lineNumberColumnDetailV2} in constant/column.js. */
export const DRAWER_LINE_NUMBER_COLUMN_WIDTH = 56;

export const ADD_ITEMS_DRAWER_PAGE_SIZE = 16;

/** 1-based line number in a filtered list across paginated drawer tables. */
export const drawerFilteredListLineNo = (page, tableIndex, pageSize = ADD_ITEMS_DRAWER_PAGE_SIZE) =>
  (Math.max(1, page) - 1) * pageSize + tableIndex + 1;

/** Ant Design Table `#` column — place first in `columns` (after rowSelection checkbox). */
export const drawerLineNumberColumn = getValue => ({
  title: "#",
  key: "line_no",
  width: DRAWER_LINE_NUMBER_COLUMN_WIDTH,
  align: "center",
  render: (_, row, index) => getValue(row, index),
});

/** PO line tables — alternating row fills (create, drawers, detail). */
export const PO_TABLE_ROW_BG = "#fff";
/** Lavender band — reads clearly vs white (mist/cloud were too close to #fff). */
export const PO_TABLE_ROW_BG_ALT = "var(--GHOST, #efeffd)";
export const PO_TABLE_ROW_BG_SELECTED = "var(--BLUE-LIGHT, #eef4ff)";
export const PO_TABLE_ROW_BG_HOVER = "var(--PEARL, #ecf1f4)";

export const drawerPickerTableStripeCss = css`
  && .ant-table-wrapper .ant-table-tbody > tr.ant-table-row > td.ant-table-cell {
    background: ${PO_TABLE_ROW_BG};
  }
  && .ant-table-wrapper .ant-table-tbody > tr.ant-table-row.order-line-table-row-striped > td.ant-table-cell {
    background: ${PO_TABLE_ROW_BG_ALT};
  }
  && .ant-table-wrapper .ant-table-tbody > tr.ant-table-row.ant-table-row-selected > td.ant-table-cell {
    background: ${PO_TABLE_ROW_BG_SELECTED};
  }
  && .ant-table-wrapper .ant-table-tbody > tr.ant-table-row:hover > td.ant-table-cell,
  && .ant-table-wrapper .ant-table-tbody > tr.ant-table-row > td.ant-table-cell.ant-table-cell-row-hover {
    background: ${PO_TABLE_ROW_BG_HOVER};
  }
`;

export const poDrawerTableStripeCss = rootSelector => `
  ${rootSelector} .ant-table-wrapper .ant-table-tbody > tr:not(.order-line-table-row-striped) > td.ant-table-cell {
    background: ${PO_TABLE_ROW_BG};
  }
  ${rootSelector} .ant-table-wrapper .ant-table-tbody > tr.order-line-table-row-striped > td.ant-table-cell {
    background: ${PO_TABLE_ROW_BG_ALT};
  }
  ${rootSelector} .ant-table-wrapper .ant-table-tbody > tr.ant-table-row-selected > td.ant-table-cell {
    background: ${PO_TABLE_ROW_BG_SELECTED};
  }
  ${rootSelector} .ant-table-wrapper .ant-table-tbody > tr:hover > td.ant-table-cell {
    background: ${PO_TABLE_ROW_BG_HOVER};
  }
`;

/** Outlined and filled actions, matching the purchase order create page buttons. */
export const poDrawerActionButtonCss = rootSelector => `
  ${rootSelector} .drawerAction {
    height: 34px !important;
    padding: 0 12px !important;
    border: 1px solid #d5deea !important;
    border-radius: 6px !important;
    background: #fff !important;
    background-color: #fff !important;
    color: #1249bf !important;
    font-size: 13px !important;
    font-weight: 600 !important;
    line-height: 20px !important;
    white-space: nowrap;
    cursor: pointer;
  }
  ${rootSelector} .drawerAction:hover {
    background: #f4f7ff !important;
    background-color: #f4f7ff !important;
    border-color: #2060ea !important;
    color: #1249bf !important;
  }
  ${rootSelector} .drawerAction.drawerActionPrimary {
    background: #2060ea !important;
    background-color: #2060ea !important;
    border-color: #2060ea !important;
    color: #fff !important;
  }
  ${rootSelector} .drawerAction.drawerActionPrimary:hover {
    background: #1249bf !important;
    background-color: #1249bf !important;
    border-color: #1249bf !important;
    color: #fff !important;
  }
  ${rootSelector} .drawerAction:disabled,
  ${rootSelector} .drawerAction:disabled:hover,
  ${rootSelector} .drawerAction.drawerActionPrimary:disabled,
  ${rootSelector} .drawerAction.drawerActionPrimary:disabled:hover {
    background: #f7f8f9 !important;
    background-color: #f7f8f9 !important;
    border-color: #e5eaf0 !important;
    color: rgba(0, 0, 0, 0.25) !important;
    cursor: not-allowed;
    opacity: 1;
  }
`;

export const AddItemsPickerTheme = createGlobalStyle`
  .${ADD_ITEMS_DRAWER_ROOT_CLASS} .ant-drawer-content {
    background: #fff;
    border-left: 1px solid var(--PEARL, #ecf1f4);
    color: var(--GREY, #4a4a68);
    font-family: Inter, sans-serif;
  }
  .${ADD_ITEMS_DRAWER_ROOT_CLASS} .ant-drawer-header {
    ${portalDrawerAntHeaderCss}
  }
  .${ADD_ITEMS_DRAWER_ROOT_CLASS} .ant-drawer-title { font-family: inherit; }
  .${ADD_ITEMS_DRAWER_ROOT_CLASS} .ant-table,
  .${ADD_ITEMS_DRAWER_ROOT_CLASS} .ant-pagination,
  .${ADD_ITEMS_DRAWER_ROOT_CLASS} .ant-alert,
  .${ADD_ITEMS_DRAWER_ROOT_CLASS} .ant-tag {
    font-family: inherit;
    font-size: 14px;
    color: var(--GREY, #4a4a68);
  }
  .${ADD_ITEMS_DRAWER_ROOT_CLASS} .ant-table-thead > tr > th {
    background: var(--MIST, #f7f8f9);
    ${tableHeaderTextCss}
    color: var(--DARK-GREY, #0e0e2c);
    border-color: var(--PEARL, #ecf1f4);
  }
  .${ADD_ITEMS_DRAWER_ROOT_CLASS} .ant-table-tbody > tr > td {
    border-color: var(--PEARL, #ecf1f4);
  }
  ${poDrawerTableStripeCss(
    `.${ADD_ITEMS_DRAWER_ROOT_CLASS}.${PO_ADD_ITEMS_DRAWER_ROOT_CLASS}`,
  )}
  .${ADD_ITEMS_DRAWER_ROOT_CLASS}:not(.${PO_ADD_ITEMS_DRAWER_ROOT_CLASS}) .ant-table-tbody > tr.ant-table-row-selected > td {
    background: var(--GHOST, #efeffd);
  }
  .${ADD_ITEMS_DRAWER_ROOT_CLASS}:not(.${PO_ADD_ITEMS_DRAWER_ROOT_CLASS}) .ant-table-tbody > tr:hover > td {
    background: var(--MIST, #f7f8f9);
  }
  .${ADD_ITEMS_DRAWER_ROOT_CLASS} .ant-table-tbody strong {
    color: var(--DARK-GREY, #0e0e2c);
    font-weight: 500;
  }
  ${poDrawerActionButtonCss(
    `.${ADD_ITEMS_DRAWER_ROOT_CLASS}.${PO_ADD_ITEMS_DRAWER_ROOT_CLASS}`,
  )}
  .${ADD_ITEMS_DRAWER_ROOT_CLASS} .ant-checkbox-checked .ant-checkbox-inner {
    background: var(--BLUE, #2060ea);
    border-color: var(--BLUE, #2060ea);
  }
  .${ADD_ITEMS_DRAWER_ROOT_CLASS} .ant-checkbox-indeterminate .ant-checkbox-inner::after {
    background: var(--BLUE, #2060ea);
  }
  .${ADD_ITEMS_DRAWER_ROOT_CLASS} .ant-pagination-item-active {
    border-color: var(--BLUE, #2060ea);
  }
  .${ADD_ITEMS_DRAWER_ROOT_CLASS} .ant-pagination-item-active a {
    color: var(--BLUE, #2060ea);
  }
  .${ADD_ITEMS_DRAWER_ROOT_CLASS} .ant-tag {
    font-size: 12px;
    border-color: var(--PEARL, #ecf1f4);
    background: var(--MIST, #f7f8f9);
  }
  .${ADD_ITEMS_DRAWER_ROOT_CLASS} .ant-progress-bg {
    background: var(--BLUE, #2060ea) !important;
  }
  .${ADD_ITEMS_DRAWER_ROOT_CLASS} .ant-alert-info {
    background: var(--CLOUD, #fcfdfe);
    border-color: var(--PEARL, #ecf1f4);
  }
  .${ADD_ITEMS_DRAWER_ROOT_CLASS} .ant-alert-info .ant-alert-icon {
    color: var(--BLUE, #2060ea);
  }
  .${ADD_ITEMS_DRAWER_ROOT_CLASS} .ant-alert-error .ant-alert-icon {
    color: var(--DANGER, #cf324a);
  }
  .${ADD_ITEMS_DRAWER_ROOT_CLASS} input[type="checkbox"] {
    accent-color: var(--BLUE, #2060ea);
  }
  .po-apply-lines-select-dropdown,
  .so-apply-lines-select-dropdown {
    z-index: 1100;
  }
`;

export const addItemsDrawerPanelStyles = {
  header: { padding: 0 },
  body: { padding: 0, overflow: "auto" },
  footer: { padding: 0 },
  mask: { backgroundColor: "rgba(0, 0, 0, 0.3)", backdropFilter: "blur(1px)" },
};

export const ADD_ITEMS_DRAWER_WIDTH = "min(100vw, max(760px, 65vw))";

export const AddItemsProductSku = styled.span`
  display: block;
  font-size: 12px;
  color: var(--ASH, #676879);
  margin-top: 2px;
`;

export const AddItemsPickerContent = styled.div`
  ${({ $striped }) => ($striped ? drawerPickerTableStripeCss : "")}
  padding: 16px;
  > p {
    color: var(--ASH, #676879);
    margin-bottom: 16px;
  }
  .toolbar {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin: 16px 0;
  }
  .selectedOnly {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
  }
  .ant-tag {
    display: block;
    width: fit-content;
    margin-top: 4px;
  }
  .productLine {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }
  .productLine .ant-tag {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    height: 20px;
    margin: 0;
    line-height: 18px;
  }
  .ant-alert {
    margin-top: 12px;
  }
`;

export const AddItemsBulkEditPanel = styled.div`
  margin-top: 16px;
  padding-top: 8px;
  border-top: 1px solid var(--PEARL, #ecf1f4);
`;

/** @param {"lines"|"items"} kind */
export const drawerSelectionPillLabel = (selectedCount, totalCount, kind = "lines") => {
  if (selectedCount > 0) {
    return `${selectedCount} out of ${totalCount} selected`;
  }
  if (kind === "items") {
    return `${totalCount} ${totalCount === 1 ? "item" : "items"}`;
  }
  return `${totalCount} ${totalCount === 1 ? "line" : "lines"}`;
};

export const AddItemsSelectionPill = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid #e5eaf0;
  background: #f7f8f9;
  color: var(--ASH, #676879);
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;

  ${({ $active }) =>
    $active &&
    css`
      border-color: #c5d9ff;
      background: var(--BLUE-LIGHT, #eef4ff);
      color: var(--DARK-GREY, #0e0e2c);
    `}
`;

export const AddItemsPickerFooter = styled(PortalFooterCard)`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid var(--PEARL, #ecf1f4);
  gap: 12px;
  flex-wrap: wrap;

  .footerButtons {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
`;

export const AddItemsSearchInput = styled(StyledFormInput)`
  margin-bottom: 0;
  &:hover,
  &:focus {
    border-color: var(--BLUE, #2060ea);
  }
  &:focus {
    outline: 1px solid var(--BLUE, #2060ea);
    outline-offset: 1px;
  }
`;

const DrawerActionButtonBase = styled(ButtonT1)`
  && {
    height: 34px;
    padding: 0 12px;
    border: 1px solid #d5deea;
    border-radius: 6px;
    background: #fff;
    background-color: #fff;
    color: #1249bf;
    font-size: 13px;
    font-weight: 600;
    line-height: 20px;
    white-space: nowrap;
    cursor: pointer;
  }

  &&:hover:not(:disabled) {
    background: #f4f7ff;
    background-color: #f4f7ff;
    border-color: #2060ea;
    color: #1249bf;
  }

  ${({ $primary }) =>
    $primary &&
    css`
      &&:not(:disabled) {
        background: #2060ea;
        background-color: #2060ea;
        border-color: #2060ea;
        color: #fff;
      }

      &&:hover:not(:disabled) {
        background: #1249bf;
        background-color: #1249bf;
        border-color: #1249bf;
        color: #fff;
      }
    `}

  &&:disabled:not([aria-busy="true"]) {
    background: #f7f8f9;
    background-color: #f7f8f9;
    border-color: #e5eaf0;
    color: rgba(0, 0, 0, 0.25);
    cursor: not-allowed;
    opacity: 1;
  }
`;

const PoDrawerButtonStyleContext = createContext(false);

export const PoDrawerButtonStyle = ({ children }) => (
  <PoDrawerButtonStyleContext.Provider value={true}>{children}</PoDrawerButtonStyleContext.Provider>
);

export const AddItemsPickerButton = ({
  children,
  type,
  size,
  onClick,
  loading,
  className,
  ...props
}) => {
  const pageStyle = useContext(PoDrawerButtonStyleContext);
  const Comp = pageStyle ? DrawerActionButtonBase : ButtonT1;
  const label = typeof children === "string" || typeof children === "number" ? children : undefined;

  return (
    <Comp
      {...props}
      $primary={type === "primary"}
      className={[
        pageStyle ? "drawerAction" : "",
        pageStyle && type === "primary" ? "drawerActionPrimary" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      type="button"
      title={label}
      loading={loading}
      loadingText={label}
      onClickCallback={onClick}
      $variant={type === "primary" ? "primary" : "secondary"}
      $size={size === "small" ? "small" : "default"}
    />
  );
};
